import { useEffect, useRef, useState } from 'react'
import addressService, {
  AutocompleteSuggestion,
  AutocompleteSuggestionDetails,
} from '../../services/addressService'
import { v4 as uuidV4 } from 'uuid'
import useCallbackDebounced from '../shared/useCallbackDebounced'
import useStableCallback from '../shared/useStableCallback'
import useCallbackDebouncedAsync from '../shared/useCallbackDebouncedAsync'
import { useStateWithRef } from '../shared/useStateWithRef'

export const useAutocompleteSuggestion = ({
  onDetailsSelected,
}: {
  onDetailsSelected: (details: AutocompleteSuggestionDetails) => void
}) => {
  const [suggestionId, setSuggestionId, suggestionIdRef] = useStateWithRef<string>()
  const stableOnDetailsSelected = useStableCallback(onDetailsSelected)
  const [suggestionDetails, setSuggestionDetails, suggestionDetailsRef] =
    useStateWithRef<AutocompleteSuggestionDetails>()
  const [error, setError] = useState<string>()
  const requestIdRef = useRef<string>()
  const fetchSuggestionDetails = useCallbackDebouncedAsync(
    async ({ id }: { id: string }) => {
      const requestId = uuidV4()
      requestIdRef.current = requestId
      setSuggestionDetails(undefined)
      await addressService
        .fetchSuggestionDetailsById({ id })
        .then(suggestionDetails => {
          // only resolve the latest request, if the user switches mode whilst loading this will prevent data cross polination
          if (requestIdRef.current === requestId) {
            setSuggestionId(undefined)
            setSuggestionDetails(suggestionDetails)
          }
        })
        .catch((error: Error) => {
          // only resolve the latest request, if the user switches mode whilst loading this will prevent data cross polination
          if (requestIdRef.current === requestId) {
            setSuggestionId(undefined)
            setError(error.message)
          }
        })
    },
    [setSuggestionDetails, setSuggestionId],
    300,
  )
  useEffect(() => {
    if (suggestionId) {
      fetchSuggestionDetails({ id: suggestionId })
    }
  }, [suggestionId, fetchSuggestionDetails])
  useEffect(() => {
    if (suggestionDetails) {
      stableOnDetailsSelected(suggestionDetails)
    }
  }, [stableOnDetailsSelected, suggestionDetails])
  const loadDetails = useCallbackDebounced(
    (suggestion: AutocompleteSuggestion) => {
      if (suggestionIdRef.current === suggestion.id) {
        if (suggestionDetailsRef.current) {
          stableOnDetailsSelected(suggestionDetailsRef.current)
        }
      } else {
        setSuggestionId(suggestion.id)
      }
    },
    [setSuggestionId, stableOnDetailsSelected, suggestionDetailsRef, suggestionIdRef],
  )
  return {
    loadDetails,
    suggestionDetails,
    loading: !!suggestionId,
    error,
  }
}

export const useAutocomplete = ({
  address,
  onDetailsSelected,
}: {
  address: string
  onDetailsSelected: (details: AutocompleteSuggestionDetails) => void
}) => {
  const { loadDetails, loading: loadingSelected } = useAutocompleteSuggestion({
    onDetailsSelected,
  })
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const requestIdRef = useRef<string>()
  const fetchSuggestions = useCallbackDebouncedAsync(
    async ({ address }: { address: string }) => {
      const requestId = uuidV4()
      requestIdRef.current = requestId
      setLoading(true)
      await addressService
        .autocomplete({ address })
        .then(suggestions => {
          // only resolve the latest request, if the user switches mode whilst loading this will prevent data cross polination
          if (requestIdRef.current === requestId) {
            setLoading(false)
            setSuggestions(suggestions)
          }
        })
        .catch((error: Error) => {
          // only resolve the latest request, if the user switches mode whilst loading this will prevent data cross polination
          if (requestIdRef.current === requestId) {
            setLoading(false)
            setError(error.message)
          }
        })
    },
    [],
    300,
  )
  useEffect(() => {
    if (address) {
      fetchSuggestions({ address })
    }
  }, [address, fetchSuggestions])
  return {
    selectSuggestion: loadDetails,
    suggestions,
    loading,
    loadingSelected,
    error,
  }
}
